/**
 * Which trust bucket the Add Payable dialog opens on, and follows to as the category is chosen.
 *
 * Pulled out of the dialog because the default it lands on decides where real money is booked, and
 * getting it wrong is silent: the payment goes out either way and only the loan's ledger disagrees.
 *
 * ## Why the default is `none`
 *
 * It used to be `constructionReserve`, which is what every payment did before the bucket field
 * existed. That is not a neutral default. The `trust_ledger` table IS the Construction Reserve tab
 * and its rows are mirrored into the Draw ledger, so a vendor invoice, a legal fee or an interest
 * payment to a lender landed on the same tab as the draws, and the draw arithmetic read them as
 * money already advanced against the rehab budget.
 *
 * CorrFirst loan 31378 is the case that settled it. A hand-keyed payout of 3,516.09 of B Piece
 * Interest to a lender, paid by ACH, posted a Disbursement row; the next draw entered on that loan
 * reported 3,516.09 previously disbursed against an eligible 141,287.50 and recommended 137,676.41,
 * short by exactly the lender's interest.
 *
 * ## Why a construction draw is the exception
 *
 * A payment categorised `Construction Draw` IS construction money, and it is the one category that
 * belongs on a construction bucket by its own name. Defaulting it to `none` would hide a real
 * draw from the tab that exists to show draws, which is the opposite failure and a worse one: a
 * missing draw understates what the borrower has taken, so the NEXT draw offers too much.
 *
 * The category is trusted here and nowhere else in this file because the server reads the very
 * same string to decide the row's type: `postPayableToLedger` writes a `Draw` row rather than a
 * `Disbursement` when `ledgerCategory === 'Construction Draw'`. So the two agree by construction,
 * and a payment that will post as a draw opens on the bucket a draw belongs in.
 *
 * ## WHICH construction bucket is the server's answer, not this file's
 *
 * It is not always the Construction Reserve. On a loan converted from a prior servicer the reserve
 * is never funded - the money is deposited into the `escrowRepairHoldback` sub-ledger, with every
 * prior draw and inspection fee against it - so a draw defaulted to the Construction Reserve posts
 * against an account holding nothing: that tab goes negative and the holdback the money actually
 * left never comes down. Longhorn 313050 ran $5,750 of draws that way; the holdback stayed at
 * $5,750 where the prior servicer reported $0.00.
 *
 * So the server names the loan's construction bucket on the payables response
 * (`constructionBucket`, from `constructionDrawBucket`) and this takes it as given. It falls back
 * to the Construction Reserve when the server says nothing, which is what the field has always
 * defaulted to and what an originated loan resolves to anyway.
 *
 * ## What this deliberately does NOT change
 *
 * Only a payment with nothing to say about its bucket. An explicit choice always wins, in both
 * directions:
 *
 *  - **An edit** carries the bucket the payment was raised with, so correcting an amount cannot
 *    move where it books.
 *  - **A server suggestion** names its own: `suspense` for a credit-balance refund (the credit IS
 *    the suspense balance), `constructionReserve` for an issued draw. Those keep arriving as they
 *    did.
 *  - **The operator's own pick**, once they touch the select. The dialog stops following the
 *    category from that point, or choosing a category would silently undo the bucket they set.
 *  - **The server's own default is untouched.** `normalizePayableLedgerBucket` still reads an
 *    ABSENT bucket as the Construction Reserve, which is what keeps every payable raised outside
 *    this dialog behaving as it does: the pay-other lines on a loan payment, the servicing-fee
 *    payout, the charge splits, and every row written before the column existed. Flipping that too
 *    would quietly stop those posting on every tenant, which is a much larger decision than the
 *    one this makes.
 */

/** Post nothing: the payment is recorded against the payee and nowhere else. */
export const NO_LEDGER_POST = 'none'

/** The Construction Reserve tab, i.e. a `trust_ledger` row. */
export const CONSTRUCTION_RESERVE = 'constructionReserve'

/**
 * The category that means construction money. Must stay spelled exactly as it is: it is a key of
 * `LEDGER_CATEGORIES`, it is what the server's draw suggestions emit, and `postPayableToLedger`
 * compares `ledgerCategory` against this same string to decide whether to write a `Draw` row.
 */
export const CONSTRUCTION_DRAW_CATEGORY = 'Construction Draw'

const text = (value?: string | null): string => String(value ?? '').trim()

/**
 * Where a payment of this category books when nobody has said otherwise. A construction draw goes
 * to whichever bucket holds this loan's construction money; everything else posts nothing.
 */
export const defaultLedgerBucketForCategory = (
  category?: string | null,
  constructionBucket?: string | null,
): string =>
  text(category) === CONSTRUCTION_DRAW_CATEGORY ? text(constructionBucket) || CONSTRUCTION_RESERVE : NO_LEDGER_POST

/**
 * What the dialog's bucket starts as, and what it moves to while the operator has not set one
 * themselves. `editing` beats `prefill` beats the category default, which is the order the other
 * fields in the dialog already resolve in.
 *
 * The first two arguments are the raw `ledgerBucket` off those objects, blank or absent when they
 * have none. Nothing is validated: the list of buckets lives in the dialog and the server refuses
 * anything outside it, so a value this does not recognise is passed through rather than silently
 * rewritten to the default, which would throw away whatever a caller meant.
 */
export const initialLedgerBucket = (
  editingBucket?: string | null,
  prefillBucket?: string | null,
  category?: string | null,
  constructionBucket?: string | null,
): string =>
  text(editingBucket) || text(prefillBucket) || defaultLedgerBucketForCategory(category, constructionBucket)
